import type { ReactNode } from 'react';
import { cn, CAR_STATUS_LABELS, DOC_TYPE_LABELS, SERVICE_TYPE_LABELS } from '../../utils/helpers';
import { t } from '../../i18n';
import type { CarStatus, DocType, ServiceType } from '../../types';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'primary';

const VARIANTS: Record<BadgeVariant, string> = {
  default: 'bg-secondary-100 text-secondary-700',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-sky-100 text-sky-800',
  primary: 'bg-primary-100 text-primary-800',
};

const DOTS: Record<BadgeVariant, string> = {
  default: 'bg-secondary-400',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-sky-500',
  primary: 'bg-primary-500',
};

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  dot?: boolean;
  className?: string;
  title?: string;
}

export function Badge({ children, variant = 'default', dot = false, className, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        VARIANTS[variant],
        className,
      )}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', DOTS[variant])} aria-hidden="true" />}
      {children}
    </span>
  );
}

const CAR_STATUS_VARIANT: Record<CarStatus, BadgeVariant> = {
  active: 'success',
  service: 'warning',
  sold: 'default',
  retired: 'default',
};

export function CarStatusBadge({ status }: { status: CarStatus }) {
  return (
    <Badge variant={CAR_STATUS_VARIANT[status]} dot={status === 'active' || status === 'service'}>
      {CAR_STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

const SERVICE_VARIANT: Record<ServiceType, BadgeVariant> = {
  maintenance: 'info',
  repair: 'warning',
  inspection: 'primary',
  tires: 'default',
  other: 'default',
};

export function ServiceTypeBadge({ type }: { type: ServiceType }) {
  return <Badge variant={SERVICE_VARIANT[type]}>{SERVICE_TYPE_LABELS[type] ?? type}</Badge>;
}

export function DocTypeBadge({ type }: { type: DocType }) {
  return <Badge variant={type === 'insurance' || type === 'inspection' ? 'primary' : 'default'}>{DOC_TYPE_LABELS[type] ?? type}</Badge>;
}

/** Colour-coded expiry: red when expired, amber within `warnDays`. */
export function ExpiryBadge({ days, date, warnDays = 30 }: { days: number | null; date: string; warnDays?: number }) {
  if (days === null) return <span className="text-secondary-400">–</span>;
  if (days < 0) return <Badge variant="danger" title={t('Neplatné pred {days} dňami', { days: -days })}>{date}</Badge>;
  if (days <= warnDays) return <Badge variant="warning" title={t('O {days} dní', { days })}>{date}</Badge>;
  return <span>{date}</span>;
}
